/**
 * Fixed-capacity Int16 PCM ring for capturing recent mic audio around a wake.
 */
export class PcmRingBuffer {
  private readonly buf: Int16Array;
  private write = 0;
  private filled = 0;

  constructor(capacitySamples: number) {
    this.buf = new Int16Array(capacitySamples);
  }

  get capacity() {
    return this.buf.length;
  }

  get length() {
    return this.filled;
  }

  push(chunk: Int16Array) {
    if (chunk.length === 0) return;
    if (chunk.length >= this.buf.length) {
      // Keep only the most recent capacity samples.
      this.buf.set(chunk.subarray(chunk.length - this.buf.length));
      this.write = 0;
      this.filled = this.buf.length;
      return;
    }

    const first = Math.min(chunk.length, this.buf.length - this.write);
    this.buf.set(chunk.subarray(0, first), this.write);
    if (first < chunk.length) {
      this.buf.set(chunk.subarray(first), 0);
    }
    this.write = (this.write + chunk.length) % this.buf.length;
    this.filled = Math.min(this.buf.length, this.filled + chunk.length);
  }

  /** Chronological copy of the samples currently in the ring. */
  snapshot(): Int16Array {
    if (this.filled === 0) return new Int16Array(0);
    if (this.filled < this.buf.length) {
      return this.buf.slice(0, this.filled);
    }
    const out = new Int16Array(this.buf.length);
    const tail = this.buf.length - this.write;
    out.set(this.buf.subarray(this.write), 0);
    out.set(this.buf.subarray(0, this.write), tail);
    return out;
  }

  clear() {
    this.write = 0;
    this.filled = 0;
  }
}

/** ~3 s of 16 kHz mono PCM. */
export const WAKE_CLIP_SAMPLES = 16_000 * 3;
